import { Clickable, Strong } from '@ahmdigital/ui';
import React from 'react';

import { outOfPocketSectionId } from '../constants';
import constants from '../../../ahm-constants';

const contactUsLink = <Clickable to={constants.URLS.SALES.CONTACT_US}>contact us</Clickable>;

const items = [
  {
    analyticsLabel: 'waiting-period',
    body: (
      <>
        It’s not just us, most insurers have a 12-month waiting period on private hospital cover for pregnancy and
        birth. If you’re thinking about getting private cover, or upgrading your cover to include pregnancy and birth
        services, you’ll need to have the appropriate cover in place <Strong>before</Strong> you become pregnant to
        ensure the waiting period has been served. If you’re admitted during your pregnancy and have not served the 12
        month waiting period at the time you’re admitted, you may not be entitled to any benefits.
        <br />
        <br />
        If you’re joining ahm from another Australian health fund at the same level of cover (e.g. Gold product tier) or
        lower but with pregnancy and birth included, you don’t need to re-serve waiting periods you’ve already served.
      </>
    ),
    title: 'More about the 12-month waiting period',
  },

  {
    analyticsLabel: 'included',
    body: (
      <>
        We have hospital cover that pays benefits towards services and treatment provided in hospital during pregnancy,
        childbirth and following delivery.
        <br />
        <br />
        This includes accommodation as a private patient in a private or public hospital (shared room only in a public
        hospital) and any fees for the labour ward, theatre or intensive care.
        <br />
        <br />
        It also includes benefits towards inpatient medical services. Medicare pays 75% of the Medicare Benefits
        Schedule (MBS) fee for your obstetrician, anaesthetist and other doctors, as well as any diagnostics (e.g.
        x-rays or lab tests) while you’re admitted to hospital, while ahm pays the remaining 25% of the MBS fee. It’s
        important to understand that some doctors and providers charge above the MBS fee and when this occurs you’ll
        have to pay the difference—this is known as the ‘gap’.
      </>
    ),
    title: 'What’s included in pregnancy and birth?',
  },

  {
    analyticsLabel: 'not-included',
    body: (
      <>
        If an excess applies to your cover, you’ll need to pay this, any personal expenses like additional meals for
        your partner or any other expenses not covered by our agreement with the hospital, as well as any
        pharmaceuticals (including drugs issued on discharge) not covered by our agreement with the hospital.
        <br />
        <br />
        Some doctors and providers charge fees for inpatient medical services that are higher than the Medicare Benefits
        Schedule (MBS) fee. When this happens, you’ll have to pay the difference—this is known as the ‘gap’. You may be
        able to reduce the gap by choosing doctors who agree to participate in ahm’s GapCover scheme for each claim as
        part of your treatment.
        <br />
        <br />
        For more information on GapCover refer to{' '}
        <Clickable to={constants.URLS.HELP.WHAT_IS_GAP_COVER}>What is GapCover?</Clickable> or{' '}
        <Clickable to={constants.URLS.FILES.MEMBER_GUIDE}>Member Guide</Clickable>.
        <br />
        <br />
        Also, private health insurers are not permitted to pay benefits towards outpatient services (when you or your
        newborn are not admitted to hospital). While you may be eligible to claim a Medicare rebate, you’ll have
        out-of-pockets for the many outpatient appointments you’re likely to have during pregnancy such as scans, tests,
        and visits with your obstetrician/GP. Also, healthy newborns are generally not admitted to hospital after the
        birth so there may also be outpatient appointments for your baby too, for example with a paediatrician.
        <br />
        <br />
        This can be a little confusing, so {contactUsLink} for more information.
      </>
    ),
    title: <span id={outOfPocketSectionId}>What’s not included in pregnancy and birth? About out-of-pocket costs</span>,
  },
  {
    analyticsLabel: 'informed-consent',
    body: (
      <>
        Informed Financial Consent is your right to know how much your treatment costs before you go to hospital. You
        can ask your doctor or obstetrician to provide a breakdown of the out-of-pocket costs you are likely to incur
        during your hospital admission. For more, read our guide to{' '}
        <Clickable to={constants.URLS.HELP.INFORMED_FINANCIAL_CONSENT}> informed financial consent</Clickable>.
      </>
    ),
    title: 'Ask for Informed Financial Consent',
  },

  {
    analyticsLabel: 'day-one',
    body: (
      <>
        Your baby isn’t automatically included on your cover as soon as they are born. You need to {contactUsLink} as
        soon as possible to add your baby to your cover.
        <br />
        <br />
        If you’re on a single cover and want to add your baby, you’ll need to change your cover type to either a family
        or single parent family cover within two months of the baby’s birth.
        <br />
        <br />
        This change to your cover will take effect from the date of birth, and any difference in premiums will be
        payable from that date. This will ensure the baby only has to serve any Waiting Periods not already served by
        the Principal Member.
        <br />
        <br />
        If you already hold a family or single parent family cover when your baby is born, all you need to do is add
        your baby to your cover within 12 months of the birth. As long as your membership existed at the baby’s date of
        birth, the child will only have to serve those Waiting Periods not already served by the Principal Member.
        <br />
        <br />
        You won’t be charged for hospital accommodation for your baby for the first 10 days of their life unless the
        baby is admitted to hospital in their own right (e.g. a special care nursery or intensive care). Generally, a
        newborn isn’t separately admitted to hospital as an inpatient (this is because the baby comes under the mother’s
        admission).
        <br />
        <br />
        With multiple births, you won’t be charged hospital accommodation for the first baby unless one or more is
        admitted as an inpatient. You will be charged for hospital accommodation for your second and any subsequent
        babies so you need to ensure they’re added to your policy from birth.
        <br />
        <br />
        Remember that your hospital daily charge and/or excess may apply for the mother and any admitted babies, up to
        the applicable limit. Check your product guide to confirm what limits apply to your cover.
        <br />
        <br />
        In some cases, a newborn may need to be admitted to hospital as an inpatient e.g. if they require treatment in a
        special care nursery or an intensive care unit. Please {contactUsLink} as soon as possible to add your baby to
        your policy to ensure your newborn will be entitled to receive any benefits under your hospital cover for these
        services.
      </>
    ),
    title: 'Ensure your baby is included in your cover from day one',
  },
  {
    analyticsLabel: 'home-birth',
    body: (
      <>
        We’ll pay $1000 for each home birth with a registered Midwife where a Medicare rebate is not payable, after
        waiting period is served.
      </>
    ),
    title: 'Want the option of a home birth?',
  },
];

export default items;
